import api from './api';
import { prepareAcquisition } from './home-acquisition';
import { getUserServiceErrorMessage } from './error_enums/user_error';

export type AuthResponse = {
  success: boolean;
  errorMessage?: string;
  data?: { accessToken?: string; refreshToken?: string; email?: string };
};

export class HomeAuthError extends Error {
  constructor(public code?: string) {
    super(getUserServiceErrorMessage(code));
  }
}

export async function authRequest(path: string, body: unknown): Promise<AuthResponse> {
  if (/^(register-user-by-email-or-mobile|register-user-email-with-password|auth\/google|auth\/apple)$/.test(path)) {
    await prepareAcquisition();
  }
  const campaign = typeof window === 'undefined' ? '' : window.pteAcquisitionHeader?.(`/user/${path}`) || '';
  const { data } = await api.post<AuthResponse>(`/user/${path}`, body, {
    headers: { Authorization: null, ...(campaign ? { 'X-Acquisition-Token': campaign } : {}) },
  });
  if (data?.success !== true) throw new HomeAuthError(data?.errorMessage);
  // Keep visit receipts until the authenticated link is acknowledged.
  return data;
}

export async function registerEmail(email: string, password: string) {
  try {
    // Registration tokens are deliberately discarded until email verification.
    await authRequest('register-user-email-with-password', { email, password });
  } catch (error) {
    if (!(error instanceof HomeAuthError)) throw error;
    if (error.code === 'USER_EMAIL_ALREADY_EXISTS') {
      const response = await api.post<{ success: boolean; data: boolean }>('/user/is-email-verified', { email }, { headers: { Authorization: null } });
      if (response.data.success !== true || response.data.data !== false) throw error;
      await resendVerification(email);
    } else if (error.code !== 'USER_EMAIL_NOT_ACTIVATED') {
      throw error;
    }
  }
}

export const resendVerification = (email: string) =>
  authRequest('send-email-mobile-verification-otp', { emailOrMobile: email });

export function verifiedSession(response: AuthResponse, email = '') {
  const accessToken = response.data?.accessToken?.trim();
  const refreshToken = response.data?.refreshToken?.trim();
  if (response.success !== true || !accessToken || !refreshToken) {
    throw new Error('Sign in could not be completed. Please try again.');
  }
  return { accessToken, refreshToken, email: response.data?.email || email };
}

// Exact configured app origin only; never turn an arbitrary URL into a login redirect.
export function authDestination(href: string, appBaseUrl: string, currentUrl: string) {
  const url = new URL(href, currentUrl);
  const app = new URL(appBaseUrl);
  if (url.origin !== app.origin || !['https:', 'http:'].includes(url.protocol)) return null;
  const mode = url.pathname === '/login' ? 'login' : 'signup';
  if (url.pathname === '/login' || url.pathname === '/signup') {
    url.pathname = '/';
    url.search = '';
  }
  url.searchParams.set('ptu_source', 'home');
  return { mode: mode as 'login' | 'signup', href: url.toString() };
}
